import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { startOfDay } from "date-fns";
import * as Yup from "yup";

import { CreateClientAppointmentRequest, clientAppointmentsApi } from "@/api";
import { AppointmentMaxLength } from "@/common/components/appointments/shared/AppointmentForm.types";
import { ALL_COUNSELORS_OPTION_ID } from "@/common/components/counselor-select/FormikCounselorSelect";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { ClientAppointmentFormValues } from "../ClientAppointmentCreation.types";
import { combineDayAndTime } from "../ClientAppointmentCreation.utils";

interface Params {
  clientId: string;
  assignedCounselorId: string | null;
  /** The client's primary phone number / e-mail address — null hides the channel. */
  firstPhoneNumber: string | null;
  firstEmail: string | null;
  onClose: () => void;
}

export function useClientAppointmentCreation({
  clientId,
  assignedCounselorId,
  firstPhoneNumber,
  firstEmail,
  onClose,
}: Params) {
  const { t } = useTranslation();
  const { showSuccess, showWarning } = useAlertContext();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (request: CreateClientAppointmentRequest) =>
      clientAppointmentsApi.createClientAppointment({ clientId, createClientAppointmentRequest: request }),
    onSuccess: (response, request) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
      // Notifications write one Aktenvermerk per channel used.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] });

      // A channel the staff member selected can come back unsent: nothing stored to send to, the
      // template renamed or deleted, a provider error. The appointment stands either way — hence a
      // warning, not an error — but reporting plain success would claim the Kund:in was informed
      // when they were not, and no Aktenvermerk exists to contradict it.
      const notificationMissed =
        (request.notifyBySms === true && !response.notifiedBySms) ||
        (request.notifyByEmail === true && !response.notifiedByEmail);

      if (notificationMissed) {
        showWarning(t("clients.client.appointmentCreation.dialog.notificationFailedMessage"));
      } else {
        showSuccess(t("clients.client.appointmentCreation.dialog.successMessage"));
      }

      onClose();
    },
  });

  const validationSchema = useMemo(() => {
    const fieldRequired = (fieldName: string) => t("common.validation.fieldRequired", { fieldName });

    return Yup.object().shape<{ [Property in keyof ClientAppointmentFormValues]?: Yup.AnySchema }>({
      // Mandatory in both modes, so these two sit outside the mode condition.
      purposeKey: Yup.string().required(fieldRequired(t("clients.client.appointmentCreation.dialog.purpose"))),
      counselorId: Yup.string()
        .nullable()
        .required(fieldRequired(t("clients.client.appointmentCreation.dialog.counselor")))
        // Only the free-slots tab offers "Alle Berater:innen"; the mode switch already drops it,
        // so this is the safety net for a hand-entered Termin, which needs exactly one owner.
        .when("mode", {
          is: "manualEntry",
          then: (schema) =>
            schema.notOneOf(
              [ALL_COUNSELORS_OPTION_ID],
              fieldRequired(t("clients.client.appointmentCreation.dialog.counselor"))
            ),
        }),
      startDay: Yup.date()
        .nullable()
        .when("mode", {
          is: "manualEntry",
          then: (schema) =>
            schema
              .required(fieldRequired(t("clients.client.appointmentCreation.dialog.startDate")))
              // `disablePast` only greys out the calendar popup — FormikDatePicker replaces MUI's own
              // error state with formik's, so a typed past date would otherwise pass unnoticed.
              .test("min-today", t("common.validation.dateMinToday"), (value) => {
                if (!(value instanceof Date) || isNaN(value.getTime())) {
                  return true;
                }
                return value >= startOfDay(new Date());
              }),
        }),
      startTime: Yup.date()
        .nullable()
        .when("mode", {
          is: "manualEntry",
          then: (schema) => schema.required(fieldRequired(t("clients.client.appointmentCreation.dialog.startTime"))),
        }),
      endDay: Yup.date()
        .nullable()
        .when("mode", {
          is: "manualEntry",
          then: (schema) => schema.required(fieldRequired(t("clients.client.appointmentCreation.dialog.endDate"))),
        }),
      endTime: Yup.date()
        .nullable()
        .when("mode", {
          is: "manualEntry",
          then: (schema) =>
            schema
              .required(fieldRequired(t("clients.client.appointmentCreation.dialog.endTime")))
              // Compared on the combined instants: the end may be on the same day at a later time
              // or on a later day at an earlier time, so neither half decides on its own.
              .test("end-after-start", t("calendar.appointmentCreation.endBeforeStart"), function () {
                const { startDay, startTime, endDay, endTime } = this.parent as ClientAppointmentFormValues;
                if (!startDay || !startTime || !endDay || !endTime) {
                  return true;
                }
                return combineDayAndTime(endDay, endTime) >= combineDayAndTime(startDay, startTime);
              }),
        }),
      location: Yup.string()
        .nullable()
        .max(AppointmentMaxLength.location, t("common.validation.maxLength", { max: AppointmentMaxLength.location })),
    });
  }, [t]);

  const formik = useFormikWithDirtyForm<ClientAppointmentFormValues>({
    initialValues: {
      mode: "freeSlots",
      purposeKey: "",
      // A Kund:in without a fixed Berater:in is exactly who "Alle Berater:innen" exists for, so the
      // dialog opens on it instead of forcing a guess at one counselor after another.
      counselorId: assignedCounselorId ?? ALL_COUNSELORS_OPTION_ID,
      selectedSlotId: null,
      startDay: null,
      startTime: null,
      endDay: null,
      endTime: null,
      location: null,
      notifyBySms: firstPhoneNumber !== null,
      notifyByEmail: false,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      // The recipients themselves are resolved server-side from the client's stored contact
      // details; the flags only say which channels the staff member selected.
      const request: CreateClientAppointmentRequest = {
        originTimeZoneId: getLocalTimeZoneId(),
        notifyBySms: values.notifyBySms && firstPhoneNumber !== null,
        notifyByEmail: values.notifyByEmail && firstEmail !== null,
        ...(values.mode === "freeSlots"
          ? { slotAppointmentId: values.selectedSlotId }
          : {
              purposeKey: values.purposeKey,
              assignedCounselorId: values.counselorId,
              startTimeUtc:
                values.startDay && values.startTime ? combineDayAndTime(values.startDay, values.startTime) : null,
              endTimeUtc: values.endDay && values.endTime ? combineDayAndTime(values.endDay, values.endTime) : null,
              location: values.location,
            }),
      };

      try {
        await createMutation.mutateAsync(request);
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  // "Termin erstellen" stays disabled until every mandatory field of the active mode is filled —
  // in manual entry that is all four period fields, not just the two days.
  const { mode, purposeKey, counselorId, selectedSlotId, startDay, startTime, endDay, endTime } = formik.values;
  const periodFilled = startDay !== null && startTime !== null && endDay !== null && endTime !== null;
  const counselorFilled = counselorId !== null && (mode === "freeSlots" || counselorId !== ALL_COUNSELORS_OPTION_ID);
  const mandatoryFieldsFilled =
    purposeKey !== "" && counselorFilled && (mode === "freeSlots" ? selectedSlotId !== null : periodFilled);
  const submitDisabled = !mandatoryFieldsFilled || createMutation.isPending;

  return { formik, submitDisabled };
}
